import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Trash2, Minus, Plus, FileText, ArrowRight } from 'lucide-react';
import { useBlueCart } from '../context/BlueCartContext';

const SWATCH = {
  'Southern Oak':'#d4b896','Maple Forest':'#c8b89a','Walnut Grove':'#8b6c4f',
  'Earth Stone':'#a09080','EIR Collection':'#b8a898','EIR':'#b8a898',
};

const MOQ_MAP = { 5.0:30000, 5.5:30000, 6.0:25000, 6.5:25000 };

export default function BlueCartDrawer({ open, onClose }) {
  const { items, itemCount, removeItem, updateQuantity } = useBlueCart();

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  const totalOrders = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position:'fixed', inset:0, zIndex:200,
        background:'rgba(22,21,18,0.45)', backdropFilter:'blur(4px)',
        opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none',
        transition:'opacity 0.3s ease',
      }}/>

      {/* Drawer */}
      <aside style={{
        position:'fixed', top:0, right:0, bottom:0, zIndex:201,
        width:'min(440px, 95vw)',
        background:'var(--ivory)', boxShadow:'-8px 0 48px rgba(22,21,18,0.18)',
        display:'flex', flexDirection:'column',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition:'transform 0.35s cubic-bezier(.4,0,.2,1)',
        overflowY:'hidden',
      }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 24px', borderBottom:'1px solid var(--rule)', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ width:9, height:9, borderRadius:'50%', background:'var(--indigo)', flexShrink:0 }}/>
            <span style={{ fontFamily:'var(--serif)', fontSize:22, color:'var(--ink)' }}>Selected Products</span>
            {itemCount>0&&<span style={{ fontFamily:'var(--sans)', fontSize:11, fontWeight:700, background:'var(--indigo)', color:'#fff', padding:'2px 8px', borderRadius:'var(--r-pill)', letterSpacing:'.06em' }}>{itemCount}</span>}
          </div>
          <button onClick={onClose} style={{ width:36, height:36, borderRadius:'50%', background:'var(--ivory-soft)', border:'1px solid var(--rule)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--ash)', transition:'background .2s' }} onMouseEnter={e=>e.currentTarget.style.background='var(--bone)'} onMouseLeave={e=>e.currentTarget.style.background='var(--ivory-soft)'}><X size={16}/></button>
        </div>

        {/* Items */}
        <div style={{ flex:1, overflowY:'auto', padding:'0 24px' }}>
          {items.length===0 ? (
            <div style={{ padding:'64px 0', textAlign:'center' }}>
              <div style={{ width:64, height:64, borderRadius:'50%', background:'var(--ivory-soft)', display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:20 }}>
                <FileText size={24} style={{ color:'var(--ash)' }}/>
              </div>
              <p style={{ fontFamily:'var(--serif)', fontSize:22, color:'var(--ink)', marginBottom:10 }}>No products selected</p>
              <p style={{ fontFamily:'var(--sans)', fontSize:13, color:'var(--ash)', lineHeight:1.6, marginBottom:28 }}>Browse the catalog and add a collection to get started.</p>
              <Link to="/catalog" onClick={onClose} className="btn btn-saffron" style={{ display:'inline-flex', alignItems:'center', gap:8, borderRadius:'var(--r-sm)' }}>Browse Collections <ArrowRight size={13}/></Link>
            </div>
          ) : (
            <div style={{ paddingTop:8 }}>
              {items.map((item, idx) => {
                const cfg = item.configuration || {};
                const totalMm = cfg.total_thickness_mm;
                const moq = MOQ_MAP[totalMm];
                const bg = SWATCH[item.product?.name] || '#d4c4b0';
                const img = item.product?.image_urls?.[0];
                return (
                  <div key={item.cartItemId} style={{ display:'flex', gap:14, padding:'18px 0', borderBottom: idx < items.length-1 ? '1px solid var(--rule)' : 'none' }}>
                    {/* Thumb */}
                    <div style={{ width:64, height:64, flexShrink:0, borderRadius:'var(--r-sm)', overflow:'hidden', background:bg }}>
                      {img&&<img src={img} alt={item.product?.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>}
                    </div>
                    {/* Info */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                        <div style={{ minWidth:0 }}>
                          <p style={{ fontFamily:'var(--serif)', fontSize:15, color:'var(--ink)', lineHeight:1.2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.product?.name}</p>
                          {cfg.s_number&&<p style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--indigo)', marginTop:3 }}>S# {cfg.s_number}</p>}
                          <div style={{ display:'flex', flexWrap:'wrap', gap:'2px 8px', marginTop:3 }}>
                            {cfg.plank_dimension&&<span style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--ash)' }}>{cfg.plank_dimension}</span>}
                            {totalMm!=null&&<span style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--ink-soft)' }}>{totalMm}mm total</span>}
                            {moq&&<span style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--saffron-deep)' }}>MOQ {moq.toLocaleString()} sq ft</span>}
                          </div>
                        </div>
                        <button onClick={()=>removeItem(item.cartItemId)} style={{ color:'var(--mist)', background:'none', border:'none', cursor:'pointer', padding:'2px 4px', flexShrink:0, transition:'color .2s' }} onMouseEnter={e=>e.currentTarget.style.color='#c0392b'} onMouseLeave={e=>e.currentTarget.style.color='var(--mist)'}><Trash2 size={13}/></button>
                      </div>
                      {/* Qty */}
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:10 }}>
                        <div style={{ display:'flex', alignItems:'center', border:'1px solid var(--rule)', borderRadius:'var(--r-sm)', overflow:'hidden', background:'var(--bone)' }}>
                          <button type="button" onClick={()=>{ if(item.quantity<=1) removeItem(item.cartItemId); else updateQuantity(item.cartItemId, item.quantity-1); }} style={{ width:30, height:30, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--ash)', background:'none', border:'none', cursor:'pointer' }}><Minus size={10}/></button>
                          <span style={{ fontFamily:'var(--mono)', fontSize:13, width:26, textAlign:'center', color:'var(--ink)' }}>{item.quantity}</span>
                          <button type="button" onClick={()=>updateQuantity(item.cartItemId, item.quantity+1)} style={{ width:30, height:30, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--ash)', background:'none', border:'none', cursor:'pointer' }}><Plus size={10}/></button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length>0&&(
          <div style={{ flexShrink:0, borderTop:'1px solid var(--rule)', padding:'20px 24px 28px', background:'var(--ivory)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'6px 0', borderBottom:'1px solid var(--rule)', marginBottom:16 }}>
              <span style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ash)' }}>Total orders queued</span>
              <span style={{ fontFamily:'var(--serif)', fontSize:22, color:'var(--ink)', fontWeight:600 }}>{totalOrders}</span>
            </div>
            <div style={{ marginBottom:12, padding:'10px 12px', background:'rgba(59,95,122,.05)', borderRadius:'var(--r-sm)', border:'1px solid rgba(59,95,122,.18)' }}>
              <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ink-soft)', lineHeight:1.5 }}>Pricing is quote-based. Confirmed wholesale rates within 1–2 business days.</p>
            </div>
            <Link to="/rfq/submit" onClick={onClose} style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, width:'100%', height:54, background:'var(--indigo)', color:'#fff', borderRadius:'var(--r-sm)', textDecoration:'none', fontFamily:'var(--sans)', fontSize:13, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', transition:'background .2s' }} onMouseEnter={e=>e.currentTarget.style.background='#2d4d63'} onMouseLeave={e=>e.currentTarget.style.background='var(--indigo)'}>
              <FileText size={16}/> Request Wholesale Pricing <ArrowRight size={14}/>
            </Link>
            <Link to="/catalog" onClick={onClose} style={{ display:'flex', alignItems:'center', justifyContent:'center', marginTop:10, width:'100%', height:40, fontFamily:'var(--sans)', fontSize:12, color:'var(--ash)', letterSpacing:'.08em', textDecoration:'none', transition:'color .2s' }} onMouseEnter={e=>e.currentTarget.style.color='var(--ink)'} onMouseLeave={e=>e.currentTarget.style.color='var(--ash)'}>Continue Browsing</Link>
          </div>
        )}
      </aside>
    </>
  );
}
